import { useRef, useState } from "react";
import type { HouseholdCreatePayload } from "@/api/households";
import { Button } from "@/components/ui/button";
import { householdError } from "../management";

type Props = {
  initial?: HouseholdCreatePayload;
  onSave: (payload: HouseholdCreatePayload) => Promise<unknown>;
  onCancel?: () => void;
  onPendingChange?: (pending: boolean) => void;
  inline?: boolean;
};
const fields = [
  ["household_name", "Household Name", 150], ["address", "Address", 255],
  ["city", "City", 100], ["state", "State", 50], ["zip_code", "ZIP / Postal Code", 20],
  ["phone", "Phone", 50], ["email", "Email", 255], ["anniversary_date", "Anniversary Date", 10],
] as const;

export function HouseholdEditor({ initial, onSave, onCancel, onPendingChange, inline = false }: Props) {
  const [values, setValues] = useState<HouseholdCreatePayload>(initial ?? { household_name: "", hide_anniversary_year: true });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const busy = useRef(false);
  const fieldset = useRef<HTMLFieldSetElement>(null);
  async function save() {
    if (busy.current) return;
    for (const input of Array.from(fieldset.current?.querySelectorAll("input") ?? [])) {
      if (!input.reportValidity()) return;
    }
    if (!values.household_name.trim()) { setError("Enter a household name."); return; }
    busy.current = true;
    setPending(true);
    onPendingChange?.(true);
    setError("");
    try {
      const payload: HouseholdCreatePayload = { household_name: values.household_name.trim(), hide_anniversary_year: values.hide_anniversary_year ?? true };
      for (const [key] of fields) if (key !== "household_name") payload[key] = values[key]?.trim() || null;
      await onSave(payload);
    } catch (error) { setError(householdError(error)); }
    finally { busy.current = false; setPending(false); onPendingChange?.(false); }
  }
  const content = <>
    <fieldset ref={fieldset} disabled={pending} className="grid gap-4 sm:grid-cols-2">
      {fields.map(([key, label, maxLength]) => <label key={key} className="space-y-2 text-sm text-slate-300">
        <span>{label}{key === "household_name" ? " *" : ""}</span>
        <input required={key === "household_name"} maxLength={maxLength}
          type={key === "email" ? "email" : key === "anniversary_date" ? "date" : "text"}
          value={values[key] ?? ""} onChange={e => setValues({ ...values, [key]: key === "household_name" ? e.target.value : e.target.value || null })}
          className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white focus:border-blue-500" />
      </label>)}
      <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
        <input type="checkbox" checked={values.hide_anniversary_year ?? true} onChange={e => setValues({ ...values, hide_anniversary_year: e.target.checked })} />
        Hide anniversary year
      </label>
    </fieldset>
    {error && <p role="alert" className="mt-4 text-sm text-rose-300">{error}</p>}
    <div className="mt-5 flex flex-wrap justify-end gap-3">
      {onCancel && <Button type="button" variant="secondary" disabled={pending} onClick={onCancel}>Cancel</Button>}
      <Button type={inline ? "button" : "submit"} disabled={pending} onClick={inline ? () => void save() : undefined}>
        {pending ? "Saving household…" : "Save Household"}
      </Button>
    </div>
  </>;
  return inline ? <div>{content}</div> : <form onSubmit={e => { e.preventDefault(); void save(); }}>{content}</form>;
}
